import {
	Box,
	Button,
	Drawer,
	Flex,
	Image,
	Stack,
	Text,
} from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ChevronDownIcon, StarFilledIcon, StarIcon } from '@/assets/icons';
import ToggleOffThumbIcon from '@/assets/icons/ToggleOffThumbIcon';
import ToggleOnThumbIcon from '@/assets/icons/ToggleOnThumbIcon';
import FaceliftAvatar from '@/components/Atoms/FaceliftAvatar/FaceliftAvatar';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftSwitch from '@/components/Atoms/FaceliftSwitch/FaceliftSwitch';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { useAppTour } from '@/hooks/useAppTour';
import usePermission from '@/hooks/usePermission';
import { useAuthStore } from '@/store';
import { useQuickActionsStore } from '@/store/quickActionsStore';
import { useTourContext } from '@/store/tourContext';
import { trackCoreClick } from '@/utils/analytics';
import { navigationItems } from '@/utils/navigationItems';
import {
	getNavItemPermission,
	getNavItemPermissionWithParent,
} from '@/utils/quickActionsUtils';

const mindsightLogoWhite = '/svgs/mindsightLogoWhite.svg';
const mindsightLogo = '/svgs/mindsightLogo.svg';
const woundcareLogo = '/svgs/woundcare-logo.svg';

const getInitials = (fullName: string) => {
	if (!fullName) return '';
	const parts = fullName.trim().split(/\s+/);
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

interface MobileDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	setExpandedItemsForTour?: (items: string[]) => void;
	expandedItemsFromTour?: string[];
	userName?: string;
	userProfileImage?: string;
	colorMode: 'light' | 'dark';
	onToggleColorMode: () => void;
}

// Drawer typing adapters (mirrors approach in Stepper.tsx)
type DrawerRootProps = React.ComponentProps<typeof Drawer.Root>;
type DrawerBackdropProps = React.ComponentProps<typeof Drawer.Backdrop>;
type DrawerPositionerProps2 = React.ComponentProps<typeof Drawer.Positioner> & {
	children?: React.ReactNode;
};
type DrawerContentProps2 = React.ComponentProps<typeof Drawer.Content> & {
	children?: React.ReactNode;
};
type DrawerCloseTriggerProps = React.ComponentProps<typeof Drawer.CloseTrigger>;
type DrawerHeaderProps = React.ComponentProps<typeof Drawer.Header>;
type DrawerTitleProps = React.ComponentProps<typeof Drawer.Title>;
type DrawerBodyProps = React.ComponentProps<typeof Drawer.Body>;
type DrawerFooterProps = React.ComponentProps<typeof Drawer.Footer>;

const D = {
	Root: (props: DrawerRootProps) => <Drawer.Root {...props} />,
	Backdrop: (props: DrawerBackdropProps) => <Drawer.Backdrop {...props} />,
	Positioner: (props: DrawerPositionerProps2) => (
		<Drawer.Positioner {...props} />
	),
	Content: (props: DrawerContentProps2) => (
		<Box as={Drawer.Content} {...props} />
	),
	CloseTrigger: (props: DrawerCloseTriggerProps) => (
		<Drawer.CloseTrigger {...props} />
	),
	Header: (props: DrawerHeaderProps) => <Drawer.Header {...props} />,
	Title: (props: DrawerTitleProps) => <Drawer.Title {...props} />,
	Body: (props: DrawerBodyProps) => <Drawer.Body {...props} />,
	Footer: (props: DrawerFooterProps) => <Drawer.Footer {...props} />,
};

const MobileDrawer: React.FC<MobileDrawerProps> = ({
	isOpen,
	onClose,
	setExpandedItemsForTour,
	expandedItemsFromTour = [],
	userName = '',
	userProfileImage = '',
	colorMode,
	onToggleColorMode,
}) => {
	const [expandedItems, setExpandedItems] = useState<string[]>([]);
	const [isAnyItemHovered, setIsAnyItemHovered] = useState(false);
	const currentPath = useLocation().pathname;
	const navigate = useNavigate();

	const { hasPermission } = usePermission();
	const { isTourActive, registerElement } = useTourContext();
	const user = useAuthStore((state) => state.user?.userInformation);
	const logout = useAuthStore((state) => state.logout);
	const { toggleQuickAction, isStarred } = useQuickActionsStore();

	// Quick action toggle state
	const [isTogglingQuickAction, setIsTogglingQuickAction] = useState(false);
	const [isSucceed, setIsSucceed] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>('');

	// Refs for tour elements
	const ordersNavItemRef = useRef<HTMLButtonElement | null>(null);
	const accountingFinanceNavItemRef = useRef<HTMLButtonElement | null>(null);
	const invoicesNavItemRef = useRef<HTMLButtonElement | null>(null);
	const openInvoicesNavItemRef = useRef<HTMLButtonElement | null>(null);
	const openInvoicesSalesRepNavItemRef = useRef<HTMLButtonElement | null>(null);
	const returnsNavItemRef = useRef<HTMLButtonElement | null>(null);
	const orderStatusNavItemRef = useRef<HTMLButtonElement | null>(null);
	const bvNavItemRef = useRef<HTMLButtonElement | null>(null);

	// Use expanded items from tour if provided and tour is active
	const activeExpandedItems =
		isTourActive && expandedItemsFromTour.length > 0
			? expandedItemsFromTour
			: expandedItems;

	// Register tour elements when drawer is open
	useAppTour({
		elements: isOpen
			? {
					ordersNavItemRef: ordersNavItemRef.current,
					accountingFinanceNavItemRef: accountingFinanceNavItemRef.current,
					invoicesNavItemRef: invoicesNavItemRef.current,
					InvoicesNavItemRef: openInvoicesNavItemRef.current,
					InvoicesSalesRepNavItemRef: openInvoicesSalesRepNavItemRef.current,
					ReturnsNavItemRef: returnsNavItemRef.current,
					OrdersNavItemRef: orderStatusNavItemRef.current,
					BVBenefitVerificationNavItemRef: bvNavItemRef.current,
				}
			: {},
	});

	// Sync expanded items with tour
	useEffect(() => {
		if (isTourActive && expandedItemsFromTour.length > 0) {
			setExpandedItems(expandedItemsFromTour);
		}
	}, [expandedItemsFromTour, isTourActive]);

	const toggleExpanded = (itemTitle: string) => {
		if (isTourActive && setExpandedItemsForTour) {
			const newItems = activeExpandedItems.includes(itemTitle)
				? activeExpandedItems.filter((item) => item !== itemTitle)
				: [...activeExpandedItems, itemTitle];
			setExpandedItemsForTour(newItems);
		} else {
			setExpandedItems((prev) =>
				prev.includes(itemTitle)
					? prev.filter((item) => item !== itemTitle)
					: [...prev, itemTitle],
			);
		}
	};

	// Theme-aware colors
	const drawerText = useColorModeValue('black', 'white');
	const primaryText = useColorModeValue('gray.800', 'gray.100');
	const secondaryText = useColorModeValue('gray.600', 'gray.300');
	const tertiaryText = useColorModeValue('gray.500', 'gray.400');
	const mindsightLogoSrc = useColorModeValue(mindsightLogo, mindsightLogoWhite);

	// Theme-aware logo - check for woundcare like TopbarLogo does
	const isWoundcare = user?.portsAvailable
		?.toLocaleLowerCase()
		.includes('woundcare');
	const logoSrc = isWoundcare ? woundcareLogo : mindsightLogoSrc;
	const logoFilter =
		isWoundcare && colorMode === 'light'
			? 'invert(1) brightness(0) saturate(100%)'
			: 'none';

	// Helper function to check if a path is active
	const isActivePath = (href?: string) => {
		if (!href) return false;
		return currentPath === href;
	};

	// Helper function to get gradient styles for active items
	const getActiveStyles = (
		isActive: boolean,
		level: 'primary' | 'secondary' | 'tertiary' = 'primary',
	) => {
		if (!isActive) return {};
		// Hide gradient when any item is hovered - restore original text color
		if (isAnyItemHovered) {
			const textColorMap = {
				primary: primaryText,
				secondary: secondaryText,
				tertiary: tertiaryText,
			};
			return {
				bg: 'transparent',
				color: textColorMap[level],
			};
		}
		return {
			bg: 'brand.solidGradient',
			color: 'white',
		};
	};

	// Helper function to handle star click
	const handleStarClick = async (
		e: React.MouseEvent,
		item: (typeof navigationItems)[0],
		parentPermission: string | null = null,
	) => {
		e.preventDefault();
		e.stopPropagation();
		if (item.href) {
			const permission = getNavItemPermissionWithParent(item, parentPermission);
			if (permission) {
				setIsTogglingQuickAction(true);
				setIsSucceed(false);
				setHasError(false);
				setErrorMessage('');

				try {
					await toggleQuickAction(item.href, permission);
					setIsTogglingQuickAction(false);
					setIsSucceed(true);
				} catch (error) {
					setIsTogglingQuickAction(false);
					setHasError(true);
					setErrorMessage(
						error instanceof Error
							? error.message
							: 'Failed to update quick actions',
					);
				}
			}
		}
	};

	return (
		<D.Root
			placement="start"
			open={isOpen}
			onOpenChange={(details: { open: boolean }) => {
				if (!details.open) onClose();
			}}
		>
			<D.Backdrop />
			<D.Positioner>
				<Box>
					<D.Content>
						<Box
							width="100%"
							bg={{ base: 'white', _dark: 'jetBlack' }}
							color={drawerText}
							boxShadow="none"
							display="flex"
							flexDirection="column"
							height="100vh"
						>
							<D.Header px={6} pt={20} pb={4}>
								<Flex align="center" justify="space-between" width="full">
									<Image
										src={logoSrc}
										alt="Mindsight Medical"
										width="180px"
										height="56px"
										objectFit="contain"
										ml={2.5}
										filter={logoFilter}
									/>
									<D.CloseTrigger />
								</Flex>
							</D.Header>

							<D.Body p={0}>
								<Box as="nav" px={6} py={6}>
									{navigationItems
										.filter(
											(el) => !el.permissions || hasPermission(el.permissions),
										)
										.map((item) => {
											if (item.submenu) {
												const isExpanded = activeExpandedItems.includes(
													item.title,
												);
												// Get ref callback based on item title
												const getMainButtonRef = (
													element: HTMLButtonElement | null,
												) => {
													if (item.title === 'Orders') {
														ordersNavItemRef.current = element;
														registerElement('ordersNavItemRef', element);
													} else if (item.title === 'Accounting & Finance') {
														accountingFinanceNavItemRef.current = element;
														registerElement('accountingFinanceNavItemRef', element);
													} else if (item.title === 'Invoices') {
														invoicesNavItemRef.current = element;
														registerElement('invoicesNavItemRef', element);
													} else if (item.title === 'Benefits Verifications') {
														bvNavItemRef.current = element;
														registerElement('BVBenefitVerificationNavItemRef', element);
													}
												};

												const needsRef = [
													'Orders',
													'Accounting & Finance',
													'Invoices',
													'Benefits Verifications',
												].includes(item.title);

												return (
													<Box key={item.title}>
													<Button
														ref={needsRef ? getMainButtonRef : undefined}
														variant="ghost"
														justifyContent="space-between"
														width="72"
														height="12"
														p={2.5}
														gap={4}
														fontSize="sm"
														lineHeight="6"
														color={primaryText}
														_hover={{
															bg: 'brand.solidGradient',
															color: 'white',
														}}
														transition="none"
														onClick={() => toggleExpanded(item.title)}
														onMouseEnter={() => setIsAnyItemHovered(true)}
														onMouseLeave={() => setIsAnyItemHovered(false)}
													>
														<Text>{item.title}</Text>
														<Box
															display="inline-flex"
															alignItems="center"
															transform={
																isExpanded
																	? 'scale(0.6) rotate(180deg)'
																	: 'scale(0.6)'
															}
															transition="transform 0.2s ease"
														>
															<ChevronDownIcon
																width={8}
																height={5}
																color="currentColor"
															/>
														</Box>
													</Button>
														<Show when={isExpanded}>
															<Box>
																{item.submenu
																	.filter(
																		(el) =>
																			!el?.permissions ||
																			el?.permissions?.length === 0 ||
																			hasPermission(el.permissions || []),
																	)
																	.map((subItem) => {
																		if (subItem.submenu) {
																			const isSubExpanded =
																				activeExpandedItems.includes(
																					`${item.title}-${subItem.title}`,
																				);
																			// Get ref callback for nested submenu (Accounting & Finance > Invoices)
																			const getNestedSubButtonRef = (
																				element: HTMLButtonElement | null,
																			) => {
																				if (
																					item.title ===
																						'Accounting & Finance' &&
																					subItem.title === 'Invoices'
																				) {
																					invoicesNavItemRef.current = element;
																					registerElement('invoicesNavItemRef', element);
																				}
																			};

																			const needsNestedRef =
																				item.title === 'Accounting & Finance' &&
																				subItem.title === 'Invoices';

																			return (
																				<Box key={subItem.title}>
																				<Button
																					ref={
																						needsNestedRef
																							? getNestedSubButtonRef
																							: undefined
																					}
																					variant="ghost"
																					justifyContent="space-between"
																					width="67"
																					height="12"
																					p={2.5}
																					gap={4}
																					ml={5}
																					fontSize="sm"
																					lineHeight="6"
																					color={secondaryText}
																					_hover={{
																						bg: 'brand.solidGradient',
																						color: 'white',
																					}}
																					transition="none"
																					onClick={() =>
																						toggleExpanded(
																							`${item.title}-${subItem.title}`,
																						)
																					}
																					onMouseEnter={() =>
																						setIsAnyItemHovered(true)
																					}
																					onMouseLeave={() =>
																						setIsAnyItemHovered(false)
																					}
																				>
																					<Text>{subItem.title}</Text>
																					<Box
																						display="inline-flex"
																						alignItems="center"
																						transform={
																							isSubExpanded
																								? 'scale(0.6) rotate(180deg)'
																								: 'scale(0.6)'
																						}
																						transition="transform 0.2s ease"
																					>
																						<ChevronDownIcon
																							width={8}
																							height={5}
																							color="currentColor"
																						/>
																					</Box>
																				</Button>
																					<Show when={isSubExpanded}>
																						<Box>
																							{subItem.submenu.map(
																								(nestedItem) => {
																									// Get ref callback for nested items (Open Invoices, etc.)
																									const getRefCallback = (
																										element: HTMLButtonElement | null,
																									) => {
																										if (
																											nestedItem.title ===
																											'Open Invoices'
																										) {
																											openInvoicesNavItemRef.current =
																												element;
																											openInvoicesSalesRepNavItemRef.current =
																												element;
																											registerElement('InvoicesNavItemRef', element);
																											registerElement('InvoicesSalesRepNavItemRef', element);
																										}
																									};

																									const nestedItemPermission =
																										getNavItemPermissionWithParent(
																											nestedItem,
																											getNavItemPermissionWithParent(
																												subItem,
																												getNavItemPermission(
																													item,
																												),
																											),
																										);
																									const isNestedItemStarred =
																										nestedItem.href
																											? isStarred(
																													nestedItem.href,
																												)
																											: false;

																									return (
																										<Button
																											key={nestedItem.title}
																											ref={
																												nestedItem.title ===
																												'Open Invoices'
																													? getRefCallback
																													: undefined
																											}
																											variant="ghost"
																											justifyContent="space-between"
																											width="60"
																											height="11"
																											p={2.5}
																											gap={2.5}
																											ml={10}
																											fontSize="sm"
																											lineHeight="6"
																											color={tertiaryText}
																											{...getActiveStyles(
																												isActivePath(
																													nestedItem.href,
																												),
																												'tertiary',
																											)}
																											_hover={{
																												bg: 'brand.solidGradient',
																												color: 'white',
																											}}
																											transition="none"
																											onClick={() => {
																												if (nestedItem.href) {
																													navigate(
																														nestedItem.href,
																													);
																													onClose();
																												}
																											}}
																											onMouseEnter={() =>
																												setIsAnyItemHovered(
																													true,
																												)
																											}
																											onMouseLeave={() =>
																												setIsAnyItemHovered(
																													false,
																												)
																											}
																										>
																											<Box>{nestedItem.title}</Box>
																											{nestedItem.href && (
																												<Box
																													display="flex"
																													alignItems="center"
																													justifyContent="center"
																													w="3"
																													flexShrink={0}
																													onClick={(e) => {
																														e.preventDefault();
																														e.stopPropagation();
																														handleStarClick(
																															e,
																															nestedItem,
																															nestedItemPermission,
																														);
																													}}
																													cursor="pointer"
																													_hover={{
																														opacity: 0.8,
																													}}
																												>
																													{isNestedItemStarred ? (
																														<StarFilledIcon
																															width={16}
																															height={16}
																															color="#FFD700"
																														/>
																													) : (
																														<StarIcon
																															width={16}
																															height={16}
																														/>
																													)}
																												</Box>
																											)}
																										</Button>
																									);
																								},
																							)}
																						</Box>
																					</Show>
																				</Box>
																			);
																		}
																		// Get ref callback for submenu items
																		const getSubItemRefCallback = (
																			element: HTMLButtonElement | null,
																		) => {
																			if (subItem.title === 'Open Invoices') {
																				openInvoicesNavItemRef.current =
																					element;
																				openInvoicesSalesRepNavItemRef.current =
																					element;
																				registerElement('InvoicesNavItemRef', element);
																				registerElement('InvoicesSalesRepNavItemRef', element);
																			} else if (
																				subItem.title === 'Return Orders'
																			) {
																				returnsNavItemRef.current = element;
																				registerElement('ReturnsNavItemRef', element);
																			} else if (
																				subItem.title === 'Order Status'
																			) {
																				orderStatusNavItemRef.current = element;
																				registerElement('OrdersNavItemRef', element);
																			}
																		};

																		const needsRef = [
																			'Open Invoices',
																			'Return Orders',
																			'Order Status',
																		].includes(subItem.title);
																		const subItemPermission =
																			getNavItemPermissionWithParent(
																				subItem,
																				getNavItemPermission(item),
																			);
																		const isSubItemStarred = subItem.href
																			? isStarred(subItem.href)
																			: false;

																		return (
																			<Button
																				key={subItem.title}
																				ref={
																					needsRef
																						? getSubItemRefCallback
																						: undefined
																				}
																				variant="ghost"
																				justifyContent="space-between"
																				width="64"
																				height="11"
																				p={2.5}
																				gap={2.5}
																				ml={5}
																				fontSize="sm"
																				lineHeight="6"
																				color={secondaryText}
																				{...getActiveStyles(
																					isActivePath(subItem.href),
																					'secondary',
																				)}
																				_hover={{
																					bg: 'brand.solidGradient',
																					color: 'white',
																				}}
																				transition="none"
																				onClick={() => {
																					if (subItem.href) {
																						navigate(subItem.href);
																						onClose();
																					}
																				}}
																				onMouseEnter={() =>
																					setIsAnyItemHovered(true)
																				}
																				onMouseLeave={() =>
																					setIsAnyItemHovered(false)
																				}
																			>
																				<Box>{subItem.title}</Box>
																				{subItem.href && (
																					<Box
																						display="flex"
																						alignItems="center"
																						justifyContent="center"
																						w="3"
																						flexShrink={0}
																						onClick={(e) => {
																							e.preventDefault();
																							e.stopPropagation();
																							handleStarClick(
																								e,
																								subItem,
																								subItemPermission,
																							);
																						}}
																						cursor="pointer"
																						_hover={{ opacity: 0.8 }}
																					>
																						{isSubItemStarred ? (
																							<StarFilledIcon
																								width={16}
																								height={16}
																								color="#FFD700"
																							/>
																						) : (
																							<StarIcon
																								width={16}
																								height={16}
																							/>
																						)}
																					</Box>
																				)}
																			</Button>
																		);
																	})}
															</Box>
														</Show>
													</Box>
												);
											}

											// Get ref callback for non-submenu items
											const getItemRef = (
												element: HTMLButtonElement | null,
											) => {
												if (item.title === 'Benefits Verifications') {
													bvNavItemRef.current = element;
													registerElement('BVBenefitVerificationNavItemRef', element);
												}
											};

											const needsItemRef =
												item.title === 'Benefits Verifications';

											return (
												<Button
													key={item.title}
													ref={needsItemRef ? getItemRef : undefined}
													variant="ghost"
													justifyContent="flex-start"
													width="72"
													height="12"
													p={2.5}
													gap={4}
													fontSize="sm"
													lineHeight="6"
													color={primaryText}
													{...getActiveStyles(
														isActivePath(item.href),
														'primary',
													)}
													_hover={{
														bg: 'brand.solidGradient',
														color: 'white',
													}}
													transition="none"
													onClick={() => {
														if (item.href) {
															navigate(item.href);
															onClose();
														}
													}}
													onMouseEnter={() => setIsAnyItemHovered(true)}
													onMouseLeave={() => setIsAnyItemHovered(false)}
												>
													{item.title}
												</Button>
											);
										})}

									{/* Quick Actions Section */}
									{(() => {
										const { quickActions } = useQuickActionsStore();
										const filteredQuickActions = useMemo(() => {
											return Object.entries(quickActions)
												.map(([href]) => {
													const findNavItemByHref = (
														items: typeof navigationItems,
													): (typeof navigationItems)[0] | null => {
														for (const item of items) {
															if (item.href === href) return item;
															if (item.submenu) {
																for (const subItem of item.submenu) {
																	if (subItem.href === href) return subItem;
																	if (subItem.submenu) {
																		for (const nestedItem of subItem.submenu) {
																			if (nestedItem.href === href) return nestedItem;
																		}
																	}
																}
															}
														}
														return null;
													};

													const navItem = findNavItemByHref(navigationItems);
													if (!navItem) return null;

													// Check permissions
													if (navItem.permissions && navItem.permissions.length > 0) {
														if (!hasPermission(navItem.permissions)) {
															return null;
														}
													}

													return navItem;
												})
												.filter((action): action is NonNullable<typeof action> => action !== null);
										}, [quickActions, hasPermission]);

										if (filteredQuickActions.length === 0) return null;

										const isQuickActionsExpanded = activeExpandedItems.includes('Quick Actions');

										return (
											<Box key="quick-actions">
												<Button
													variant="ghost"
													justifyContent="space-between"
													width="72"
													height="12"
													p={2.5}
													gap={4}
													fontSize="sm"
													lineHeight="6"
													color={primaryText}
													_hover={{
														bg: 'brand.solidGradient',
														color: 'white',
													}}
													transition="none"
													onClick={() => toggleExpanded('Quick Actions')}
													onMouseEnter={() => setIsAnyItemHovered(true)}
													onMouseLeave={() => setIsAnyItemHovered(false)}
												>
													<Text>Quick Actions</Text>
													<Box
														display="inline-flex"
														alignItems="center"
														transform={
															isQuickActionsExpanded
																? 'scale(0.6) rotate(180deg)'
																: 'scale(0.6)'
														}
														transition="transform 0.2s ease"
													>
														<ChevronDownIcon
															width={8}
															height={5}
															color="currentColor"
														/>
													</Box>
												</Button>
												<Show when={isQuickActionsExpanded}>
													<Box>
														{filteredQuickActions.map((action) => {
															const actionPermission = action.permissions?.[0] || null;
															const isActionStarred = action.href
																? isStarred(action.href)
																: false;

															return (
																<Button
																	key={action.href || action.title}
																	variant="ghost"
																	justifyContent="space-between"
																	width="64"
																	height="11"
																	p={2.5}
																	gap={2.5}
																	ml={5}
																	fontSize="sm"
																	lineHeight="6"
																	color={secondaryText}
																	{...getActiveStyles(
																		isActivePath(action.href),
																		'secondary',
																	)}
																	_hover={{
																		bg: 'brand.solidGradient',
																		color: 'white',
																	}}
																	transition="none"
																	onClick={() => {
																		if (action.href) {
																			navigate(action.href);
																			onClose();
																		}
																	}}
																	onMouseEnter={() => setIsAnyItemHovered(true)}
																	onMouseLeave={() => setIsAnyItemHovered(false)}
																>
																	<Box>{action.title}</Box>
																	{action.href && (
																		<Box
																			display="flex"
																			alignItems="center"
																			justifyContent="center"
																			w="3"
																			flexShrink={0}
																			onClick={(e) => {
																				e.preventDefault();
																				e.stopPropagation();
																				handleStarClick(e, action, actionPermission);
																			}}
																			cursor="pointer"
																			_hover={{ opacity: 0.8 }}
																		>
																			{isActionStarred ? (
																				<StarFilledIcon
																					width={16}
																					height={16}
																					color="#FFD700"
																				/>
																			) : (
																				<StarIcon width={16} height={16} />
																			)}
																		</Box>
																	)}
																</Button>
															);
														})}
													</Box>
												</Show>
											</Box>
										);
									})()}
								</Box>

								<Flex flex="1" />
							</D.Body>

							{/* Divider */}
							<Box
								width="full"
								height="1px"
								bg={{ base: 'gray.200', _dark: 'gray.700' }}
								my={2}
							/>

							<D.Footer px={6} pb={6}>
								<Stack gap={0} width="full">
									{/* Theme Toggle Section */}
									<Flex
										alignItems="center"
										justifyContent="space-between"
										width="full"
										py={3}
										px={2}
									>
										<Flex alignItems="center" gap={3}>
											<Box
												p={2}
												borderRadius="8px"
												bg={{ base: 'gray.100', _dark: 'gray.700' }}
											>
												<Show when={colorMode === 'dark'}>
													<ToggleOnThumbIcon />
												</Show>
												<Show when={colorMode !== 'dark'}>
													<ToggleOffThumbIcon />
												</Show>
											</Box>
											<Box>
												<FaceliftText
													fontSize="sm"
													fontWeight="semibold"
													color={primaryText}
													lineHeight="1.2"
												>
													Theme
												</FaceliftText>
												<FaceliftText
													fontSize="xs"
													color={secondaryText}
													lineHeight="1.2"
												>
													{colorMode === 'dark' ? 'Dark Mode' : 'Light Mode'}
												</FaceliftText>
											</Box>
										</Flex>
										<FaceliftSwitch
											checked={colorMode === 'dark'}
											onCheckedChange={onToggleColorMode}
											unCheckedThumbIcon={<ToggleOffThumbIcon />}
											checkedThumbIcon={<ToggleOnThumbIcon />}
										/>
									</Flex>

									{/* Profile Section */}
									<Flex
										alignItems="center"
										justifyContent="space-between"
										width="full"
										py={3}
										px={2}
										cursor="pointer"
										onClick={() => {
											navigate('/profile');
											onClose();
										}}
										_hover={{ opacity: 0.8 }}
										transition="opacity 0.2s ease-in-out"
									>
										<Flex alignItems="center" gap={3} flex="1" minW="0">
											<FaceliftAvatar
												size="sm"
												name={getInitials(userName)}
												previewUrl={userProfileImage}
											/>
											<Box minW="0" flex="1">
												<FaceliftText
													fontSize="sm"
													fontWeight="semibold"
													color={primaryText}
													overflow="hidden"
													textOverflow="ellipsis"
													whiteSpace="nowrap"
													lineHeight="1.2"
												>
													{userName}
												</FaceliftText>
												<FaceliftText
													fontSize="xs"
													color={secondaryText}
													lineHeight="1.2"
												>
													Profile
												</FaceliftText>
											</Box>
										</Flex>
									</Flex>

									<Box pt={2}>
										<FaceliftButton
											width="full"
											height="12"
											onClick={async () => {
												trackCoreClick('logout_clicked', {
													source_module: 'mobile_drawer',
												});
												// Await logout before navigation
												await logout();
												navigate('/login?logout=true', { replace: true });
												onClose();
											}}
											sx={{ borderRadius: 12 }}
										>
											Log Out
										</FaceliftButton>
									</Box>
								</Stack>
							</D.Footer>
						</Box>
					</D.Content>
				</Box>
			</D.Positioner>

			{/* Quick Action Toggle Modal */}
			<FaceliftSuccessProgressModal
				open={isTogglingQuickAction || isSucceed || hasError}
				isLoading={isTogglingQuickAction}
				isSucceed={isSucceed}
				hasError={hasError}
				onOpenChange={(open: boolean) => {
					if (!open) {
						setIsTogglingQuickAction(false);
						setIsSucceed(false);
						setHasError(false);
						setErrorMessage('');
					}
				}}
				title="Quick Action Updated!"
				errorMessage={errorMessage}
			/>
		</D.Root>
	);
};

export default MobileDrawer;
